import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, Search, Filter, ChevronRight, ChevronDown,
  Clock, CheckCircle2, Factory, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ItemCodeLabel from '../components/ItemCodeLabel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

/**
 * Global Lot History — every lot ever received, with vendor / material / status filters
 * and per-lot batch consumption breakdown.
 *
 * Each row expands to show: which batches consumed how much of THIS lot, plus
 * batch product code + product name + total cost contributed.
 */
export default function LotHistory() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const canSeePrices = isAdmin || user?.permissions?.can_see_prices;

  const [data, setData] = useState({ lots: [], vendors: [], materials: [], total_count: 0, active_count: 0, consumed_count: 0 });
  const [turnaround, setTurnaround] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (vendorFilter) params.append('vendor', vendorFilter);
      if (materialFilter) params.append('material', materialFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/inventory/lot-index?${params}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load lot history');
    } finally { setLoading(false); }
  };

  // Debounced reload on filter changes
  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, vendorFilter, materialFilter, statusFilter]);

  // Fetch lot-turnaround once on mount (sourced from procurement-ai dashboard
  // which already aggregates this server-side)
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/procurement-ai/dashboard`, { withCredentials: true })
      .then(r => setTurnaround(r.data?.lot_turnaround || {}))
      .catch(() => {});
  }, [isAdmin]);

  // Aggregate average across all materials with samples > 0
  const overallTurnaround = useMemo(() => {
    const rows = Object.values(turnaround).filter(t => t.samples && t.avg_days != null);
    if (!rows.length) return null;
    const totalSamples = rows.reduce((s, r) => s + r.samples, 0);
    const weighted = rows.reduce((s, r) => s + r.avg_days * r.samples, 0);
    return {
      avgDays: Math.round(weighted / totalSamples * 10) / 10,
      lots: totalSamples,
      materialsWithHistory: rows.length,
      fastest: Math.min(...rows.map(r => r.fastest ?? 9999)),
      slowest: Math.max(...rows.map(r => r.slowest ?? 0)),
    };
  }, [turnaround]);

  return (
    <div className="space-y-4" data-testid="lot-history-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <Package className="text-amber-400" size={22} /> Lot History
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Every lot ever received · search by lot no / material / vendor · expand a row to see which batches consumed it
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="refresh-lot-index"
          className="text-xs px-3 py-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 flex items-center gap-1.5">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Lots',   value: data.total_count,    color: 'text-zinc-200' },
          { label: 'Active Lots',  value: data.active_count,   color: 'text-green-300' },
          { label: 'Finished',     value: data.consumed_count, color: 'text-zinc-400' },
        ].map(s => (
          <div key={s.label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider">{s.label}</div>
            <div className={`text-lg font-mono font-bold mt-1 ${s.color}`}>{s.value}</div>
          </div>
        ))}
        {isAdmin && overallTurnaround && (
          <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border border-blue-800/40 rounded-xl p-3" data-testid="turnaround-card">
            <div className="text-blue-300 text-[10px] uppercase tracking-wider flex items-center gap-1">
              <Clock size={11} /> Lot Turnaround
            </div>
            <div className="text-lg font-mono font-bold mt-1 text-blue-200">
              avg {overallTurnaround.avgDays} <span className="text-xs text-blue-400">days</span>
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">
              receive → finished · {overallTurnaround.lots} lots · range {overallTurnaround.fastest}–{overallTurnaround.slowest}d
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="lot-search"
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by lot no, material, or vendor…"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            data-testid="lot-status-filter"
            className="bg-zinc-950 border border-zinc-800 rounded-lg px-2.5 py-2 text-zinc-200 text-xs focus:outline-none">
            <option value="all">All status</option>
            <option value="active">Active only</option>
            <option value="consumed">Consumed / Finished only</option>
          </select>
          <select value={materialFilter} onChange={e => setMaterialFilter(e.target.value)}
            data-testid="lot-material-filter"
            className="bg-zinc-950 border border-zinc-800 rounded-lg px-2.5 py-2 text-zinc-200 text-xs focus:outline-none max-w-44 truncate">
            <option value="">All materials</option>
            {data.materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
            data-testid="lot-vendor-filter"
            className="bg-zinc-950 border border-zinc-800 rounded-lg px-2.5 py-2 text-zinc-200 text-xs focus:outline-none max-w-44 truncate">
            <option value="">All vendors</option>
            {data.vendors.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          {(search || vendorFilter || materialFilter || statusFilter !== 'all') && (
            <button onClick={() => { setSearch(''); setVendorFilter(''); setMaterialFilter(''); setStatusFilter('all'); }}
              className="text-xs text-amber-400 hover:text-amber-300 px-2">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Lot table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-zinc-500 text-sm py-12 text-center">Loading lot history…</div>
        ) : data.lots.length === 0 ? (
          <div className="text-zinc-500 text-sm py-12 text-center">No lots match these filters</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-zinc-500 text-xs">
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="text-left px-3 py-2">Lot No</th>
                  <th className="text-left px-3 py-2">Material</th>
                  <th className="text-left px-3 py-2">Vendor</th>
                  <th className="text-left px-3 py-2">Vendor Inv/Bill No</th>
                  <th className="text-left px-3 py-2">Received</th>
                  <th className="text-left px-3 py-2">Finished</th>
                  <th className="text-right px-3 py-2">Orig Qty</th>
                  <th className="text-right px-3 py-2">Used</th>
                  <th className="text-right px-3 py-2">Remaining</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-center px-3 py-2">Used in</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {data.lots.map(lot => {
                  const isExp = expanded[lot.id];
                  const Icon = lot.is_active ? Clock : CheckCircle2;
                  const statusColor = lot.is_active
                    ? (lot.fifo_priority ? 'text-red-400' : 'text-green-400')
                    : 'text-zinc-500';
                  return (
                    <React.Fragment key={lot.id}>
                      <tr
                        onClick={() => setExpanded(s => ({ ...s, [lot.id]: !s[lot.id] }))}
                        className={`hover:bg-zinc-800/40 cursor-pointer ${!lot.is_active ? 'opacity-70' : ''}`}
                        data-testid={`lot-row-${lot.id}`}>
                        <td className="px-2 py-2 text-zinc-500 text-center">
                          {(lot.batch_count > 0 || lot.sales_count > 0) ? (isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-mono text-[11px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded-md">
                            {lot.lot_no || '—'}
                          </span>
                          {lot.fifo_priority && <span className="ml-1 text-[10px] text-red-400" title="FIFO priority">⚡</span>}
                        </td>
                        <td className="px-3 py-2 text-zinc-200 text-xs"><ItemCodeLabel name={lot.material_name} /></td>
                        <td className="px-3 py-2 text-zinc-400 text-xs">{lot.vendor_name || '—'}</td>
                        <td className="px-3 py-2 text-xs font-mono" data-testid={`lot-invoice-no-${lot.id}`}>
                          {lot.source_invoice_number
                            ? <span className="text-emerald-400" title="Linked to vendor purchase bill">{lot.source_invoice_number}</span>
                            : (lot.bill_ref
                                ? <span className="text-zinc-400" title="Manual bill ref">{lot.bill_ref}</span>
                                : <span className="text-zinc-600">—</span>)}
                        </td>
                        <td className="px-3 py-2 text-zinc-400 text-xs font-mono">{fmtDate(lot.purchase_date)}</td>
                        <td className="px-3 py-2 text-xs font-mono">
                          {lot.finished_at
                            ? <span className="text-zinc-500">{fmtDate(lot.finished_at)}</span>
                            : lot.is_active
                              ? <span className="text-green-400 font-semibold">in use</span>
                              : <span className="text-zinc-600">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-zinc-400 text-xs">{lot.quantity_original} {lot.unit || ''}</td>
                        <td className="px-3 py-2 text-right font-mono text-amber-400 text-xs">{lot.quantity_consumed} {lot.unit || ''}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs font-semibold ${lot.is_active ? 'text-zinc-100' : 'text-zinc-600'}`}>
                          {lot.quantity_remaining} {lot.unit || ''}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] flex items-center gap-1 ${statusColor}`}>
                            <Icon size={11} /> {lot.is_active ? (lot.fifo_priority ? 'Priority' : 'Active') : 'Finished'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex flex-wrap items-center justify-center gap-1">
                            {lot.batch_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-blue-900/30 text-blue-300 border border-blue-800/40">{lot.batch_count} batch{lot.batch_count === 1 ? '' : 'es'}</span>}
                            {lot.sales_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-900/30 text-emerald-300 border border-emerald-800/40" data-testid={`lot-sales-badge-${lot.id}`}>{lot.sales_count} sale{lot.sales_count === 1 ? '' : 's'} · {lot.qty_sold}</span>}
                            {!lot.batch_count && !lot.sales_count && <span className="text-zinc-600 text-[10px]">—</span>}
                          </div>
                        </td>
                      </tr>
                      {isExp && lot.sales_count > 0 && (
                        <tr>
                          <td colSpan={12} className="bg-zinc-950/60 px-6 py-3" data-testid={`lot-sales-${lot.id}`}>
                            <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-2">Sold directly on sales invoices (raw material sale)</div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs min-w-[520px]">
                                <thead className="text-zinc-600">
                                  <tr>
                                    <th className="text-left px-2 py-1.5">Invoice</th>
                                    <th className="text-left px-2 py-1.5">Customer</th>
                                    <th className="text-left px-2 py-1.5">Date</th>
                                    <th className="text-right px-2 py-1.5">Qty from this lot</th>
                                    <th className="text-right px-2 py-1.5">Rate</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-800/60">
                                  {lot.sales.map((sl, i) => (
                                    <tr key={`${sl.voucher_id}-${i}`} className="hover:bg-zinc-800/40" data-testid={`lot-sale-row-${sl.voucher_id}`}>
                                      <td className="px-2 py-1.5 font-mono text-amber-300">{sl.invoice_number || sl.voucher_number}</td>
                                      <td className="px-2 py-1.5 text-zinc-200">{sl.client || '—'}</td>
                                      <td className="px-2 py-1.5 text-zinc-500 font-mono">{sl.date || '—'}</td>
                                      <td className="px-2 py-1.5 text-right font-mono text-emerald-300 font-semibold">{sl.qty_used} {lot.unit || ''}</td>
                                      <td className="px-2 py-1.5 text-right font-mono text-zinc-400">{sl.rate ? `₹${sl.rate}` : '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                      {isExp && lot.batch_count > 0 && (
                        <tr>
                          <td colSpan={12} className="bg-zinc-950/60 px-6 py-3" data-testid={`lot-batches-${lot.id}`}>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                              <Factory size={11} /> Batches that consumed this lot
                            </div>
                            <table className="w-full text-xs">
                              <thead className="text-zinc-600">
                                <tr>
                                  <th className="text-left px-2 py-1.5">Batch ID</th>
                                  <th className="text-left px-2 py-1.5">Product Code</th>
                                  <th className="text-left px-2 py-1.5">Product Name</th>
                                  <th className="text-right px-2 py-1.5">Batch Qty</th>
                                  <th className="text-right px-2 py-1.5">Qty Used from this lot</th>
                                  <th className="text-left px-2 py-1.5">Status</th>
                                  <th className="text-left px-2 py-1.5">When</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-zinc-800/60">
                                {lot.batches.map((b, i) => (
                                  <tr key={`${b.batch_id}-${i}`} className="hover:bg-zinc-800/40" data-testid={`batch-usage-${b.batch_id}`}>
                                    <td className="px-2 py-1.5 font-mono text-zinc-400">{b.batch_id}</td>
                                    <td className="px-2 py-1.5">
                                      {b.product_code
                                        ? <span className="font-mono text-amber-400">{b.product_code}</span>
                                        : <span className="text-zinc-600">—</span>}
                                    </td>
                                    <td className="px-2 py-1.5 text-zinc-200">{b.product_name || '—'}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-zinc-400">{b.batch_qty}</td>
                                    <td className="px-2 py-1.5 text-right font-mono text-amber-300 font-semibold">{b.qty_used} {lot.unit || ''}</td>
                                    <td className="px-2 py-1.5">
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${b.status === 'Completed' ? 'bg-green-900/30 text-green-300 border border-green-700/40' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}>
                                        {b.status || '—'}
                                      </span>
                                    </td>
                                    <td className="px-2 py-1.5 text-zinc-500 font-mono">{fmtDate(b.completed_at || b.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
